import React from "react";
import {
     Box,
     Typography,
     Accordion,
     AccordionSummary,
     AccordionDetails,
     Checkbox,
     Badge,
     IconButton,
     Tooltip,
} from "@mui/material";
import {
     ExpandMore as ExpandMoreIcon,
     MoreVert as MoreVertIcon,
     ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
     WarningAmberOutlined as WarningAmberOutlinedIcon,
     ChatBubbleOutline as ChatBubbleOutlineIcon,
} from "@mui/icons-material";
import { CombinedMilestone, CombinedMilestoneTask } from "../types";
import { DivisionMileStone } from "../../../../../../../../../api/divisionMileStones/types";
import { Note } from "../../../../../../../../../api/homeSiteNotes/types";
import { Exception } from "../../../../../../../../../api/homeSiteExceptions/types";
import { Alert } from "../../../../../../../../../api/homeSiteAlerts/types";
import { ALERT_ACTIVE_STATUS, ALERTS, EXCEPTIONS, NOTES } from "../../../../../../../../../utils/constants";
import MilestoneTaskList from "./MilestoneTaskList";
import { milestoneStyles } from "../styles/milestoneStyles";
import { getMilestoneCheckboxState, isMilestoneIndeterminate, isMilestoneStrikeThrough, isMilestoneDisabled } from "../utils/milestoneUtils";

interface MilestoneAccordionProps {
     milestone: CombinedMilestone;
     expanded: boolean;
     toggleAccordion: (id: string) => void;
     selectedMilestones: { [key: string]: boolean };
     handleCheckboxChange: (id: string) => void;
     handleMenuClick: (
          event: React.MouseEvent<HTMLElement>,
          milestoneId: string
     ) => void;
     taskStates: { [key: string]: boolean };
     handleTaskCheckboxChange: (task: CombinedMilestoneTask) => void;
     getTasksForMilestone: (milestoneName: string) => CombinedMilestoneTask[];
     exceptionCounts: Exception[];
     alertsCounts: Alert[];
     notesCounts: Note[];
     userId: string;
     isNotApplicableMilestone: (milestoneName: string) => boolean;
     updateMilestoneTasks: any;
     automatedTasks: DivisionMileStone[];
     saleDate: string | undefined | null;
     closeOfEscrowActual: string | undefined | null;
     closeOfEscrowEstimated: string | undefined | null;
     setMilestoneTab?: (tab: string) => void;
     homeSiteId: string;
     divisionCode: string;
}

const MilestoneAccordion: React.FC<MilestoneAccordionProps> = ({
     milestone,
     expanded,
     toggleAccordion,
     selectedMilestones,
     handleCheckboxChange,
     handleMenuClick,
     taskStates,
     handleTaskCheckboxChange,
     getTasksForMilestone,
     exceptionCounts,
     alertsCounts,
     notesCounts,
     userId,
     isNotApplicableMilestone,
     updateMilestoneTasks,
     automatedTasks,
     saleDate,
     closeOfEscrowActual,
     closeOfEscrowEstimated,
     setMilestoneTab,
     homeSiteId,
     divisionCode,
}) => {
     // Extract counts for the current milestone
     const getCount = (data: any[] | undefined, milestoneName: string, statusKey: string, statusValue: boolean | string, noteType?: string) => {
          if (!data || !Array.isArray(data)) return 0;
          return (
               data.filter(
                    (item) =>
                         item.mileStoneName === milestoneName &&
                         item[statusKey] === statusValue &&
                         item.noteType === noteType
               ).length || 0
          );
     };

     const exceptionCount = getCount(exceptionCounts, milestone.milestoneName, "status", true);
     const alertCount = getCount(alertsCounts, milestone.milestoneName, "status", ALERT_ACTIVE_STATUS);
     const noteCount = getCount(notesCounts, milestone.milestoneName, "status", true, "homeSiteNotes");

     return (
          <Accordion
               expanded={expanded}
               onChange={(event, isExpanded) => {
                    event.stopPropagation();
                    toggleAccordion(milestone.milestoneName);
               }}
               sx={{
                    ...milestoneStyles.accordion,
                    "&:before": {
                         display: "none",
                    },
                    "&.Mui-expanded": {
                         margin: 1,
                    },
                    margin: 1,
               }}
          >
               <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={milestoneStyles.accordionSummary}
               >
                    <Box sx={milestoneStyles.titleContainer}>
                         <Checkbox
                              checked={getMilestoneCheckboxState(milestone)}
                              indeterminate={isMilestoneIndeterminate(milestone)}
                              onChange={() => handleCheckboxChange(milestone.milestoneName)}
                              onClick={(event) => event.stopPropagation()}
                              disabled={isMilestoneDisabled(milestone)}
                              sx={
                                   isMilestoneDisabled(milestone)
                                        ? milestoneStyles.indeterminate
                                        : milestoneStyles.checkbox
                              }
                         />
                         <Typography
                              sx={{
                                   ...milestoneStyles.milestoneTitle,
                                   textDecoration: isMilestoneStrikeThrough(milestone)
                                        ? "line-through"
                                        : "none",
                                   cursor: "auto",
                              }}
                         >
                              {milestone.milestoneName}
                         </Typography>
                    </Box>
                    <Box sx={milestoneStyles.iconContainer}>
                         <Badge
                              badgeContent={exceptionCount}
                              sx={{ ...milestoneStyles.badge, ...milestoneStyles.exceptionBadge }}
                              onClick={(event) => {
                                   event.stopPropagation();
                                   setMilestoneTab?.(EXCEPTIONS);
                              }}
                              max={999}
                         >
                              <WarningAmberOutlinedIcon
                                   sx={{ ...milestoneStyles.icon, color: "#C93939" }}
                              />
                         </Badge>
                         <Badge
                              badgeContent={alertCount}
                              sx={milestoneStyles.alertBadge}
                              onClick={(event) => {
                                   event.stopPropagation();
                                   setMilestoneTab?.(ALERTS);
                              }}
                              max={999}
                         >
                              <ErrorOutlineOutlinedIcon
                                   sx={{ ...milestoneStyles.icon, color: "#c58026" }}
                              />
                         </Badge>
                         <Badge
                              badgeContent={noteCount}
                              sx={milestoneStyles.noteBadge}
                              onClick={(event) => {
                                   event.stopPropagation();
                                   setMilestoneTab?.(NOTES);
                              }}
                              max={999}
                         >
                              <ChatBubbleOutlineIcon sx={{ ...milestoneStyles.icon, color: "#3F63C0" }}
                              />
                         </Badge>
                         {!isMilestoneDisabled(milestone) && (
                              <IconButton
                                   size="small"
                                   onClick={(event) => {
                                        event.stopPropagation();
                                        handleMenuClick(event, milestone.milestoneName);
                                   }}
                              >
                                   <Tooltip title="Menu">
                                        <MoreVertIcon />
                                   </Tooltip>
                              </IconButton>
                         )}
                    </Box>
               </AccordionSummary>
               <AccordionDetails>
                    <MilestoneTaskList
                         userId={userId}
                         tasks={getTasksForMilestone(milestone.milestoneName)}
                         taskStates={taskStates}
                         handleTaskCheckboxChange={handleTaskCheckboxChange}
                         updateMilestoneTasks={updateMilestoneTasks}
                         automatedTasks={automatedTasks}
                         saleDate={saleDate}
                         closeOfEscrowActual={closeOfEscrowActual}
                         closeOfEscrowEstimated={closeOfEscrowEstimated}
                         homeSiteId={homeSiteId}
                         divisionCode={divisionCode}
                    />
               </AccordionDetails>
          </Accordion>
     );
};

export default MilestoneAccordion; 